/**
 * Availability button group component for filtering by stock status
 * Filters warehouse rows based on pickup availability and ETA dates
 */

import type { WarehouseResult } from "../../types/apiTypes.js";
import type { LegacyWarehouse } from "../../utils/warehouseAdapter.js";
import { applyUniformUpdateCartButtonHeight } from "./pricingGrid.js";

// State tracking
let cachedTransitResults: WarehouseResult[] | null = null;
let currentFilterType = "week";
let currentFilterDate: string | undefined;

/**
 * Get tomorrow's date at midnight (local time)
 */
function getTomorrowDate(): Date {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
}

/**
 * Parse ISO date string to Date object (date only, no time)
 */
function parseDate(dateStr: string | null): Date | null {
  if (!dateStr) return null;
  const datePart = dateStr.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format date as "DDD MM/DD" (e.g., "FRI 12/21")
 */
function formatDateLabel(date: Date): string {
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const dayName = days[date.getDay()];
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${dayName} ${month}/${day}`;
}

/**
 * Check if a warehouse has inventory for any product on the page
 */
function warehouseHasInventory(warehouseCode: string): boolean {
  let hasInventory = false;
  $(`.warehouse_row[data-warehouse-code="${warehouseCode}"]`).each(function (): false | void {
    const $row = $(this);
    const $variantCells = $row.find(".variant_cell").not(".variant_cell_empty");
    const hasQuantityCells = $variantCells.not(".no-quantity").length > 0;
    if (hasQuantityCells) {
      hasInventory = true;
      return false; // Break out of .each()
    }
  });
  return hasInventory;
}

/**
 * Get all pickup-available warehouse codes
 */
function getPickupWarehouseCodes(): string[] {
  if (cachedTransitResults && Array.isArray(cachedTransitResults)) {
    return cachedTransitResults
      .filter((result) => result.pickupAvailable === true)
      .map((result) => result.warehouseCode);
  }
  if (!window.warehouseData) return [];
  return window.warehouseData
    .filter((wh: LegacyWarehouse) => wh.PickupAvailable && wh.Enabled)
    .map((wh: LegacyWarehouse) => wh.Code);
}

// ⚠ The pickup filter used to OVERWRITE a stocking row's transit text with its own browser-side
// arithmetic ("Order by 3:30 PM CT for pickup today" / "Order for pickup tomorrow") — no business-day
// check, so it said "pickup today" on a Saturday and "tomorrow" on a Friday evening. The row now
// always carries the server's own pickup time (zipCodeInput.ts updateTransitTimeDisplay), so the
// filter only shows and hides rows. scanner-dev "Pickup Ready Time Promise.md" D7.

/**
 * Collect unique ETA dates from warehouse rows that are after tomorrow and within 7 days.
 * Dates beyond 7 days are covered by the "Later" (all) button and not shown individually.
 */
function collectFutureETADates(): Date[] {
  const tomorrow = getTomorrowDate();
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  sevenDaysFromNow.setHours(23, 59, 59, 999);

  const dateSet = new Set<string>();

  $(".warehouse_row").each(function () {
    const $row = $(this);
    const deliveryDateStr = $row.attr("data-estimated-delivery");
    if (!deliveryDateStr) return;

    const deliveryDate = parseDate(deliveryDateStr);
    if (!deliveryDate) return;

    if (deliveryDate > tomorrow && deliveryDate <= sevenDaysFromNow) {
      dateSet.add(deliveryDateStr.split("T")[0]);
    }
  });

  return Array.from(dateSet)
    .map((str) => parseDate(str))
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime());
}

/**
 * Check if any warehouse can deliver by tomorrow
 */
function hasWarehouseDeliveryByTomorrow(): boolean {
  const tomorrow = getTomorrowDate();
  let found = false;

  $(".warehouse_row").each(function (): false | void {
    const $row = $(this);
    const deliveryDateStr = $row.attr("data-estimated-delivery");
    if (!deliveryDateStr) return;

    const deliveryDate = parseDate(deliveryDateStr);
    if (deliveryDate && deliveryDate <= tomorrow) {
      const warehouseCode = $row.attr("data-warehouse-code");
      if (warehouseCode && warehouseHasInventory(warehouseCode)) {
        found = true;
        return false; // Break
      }
    }
  });

  return found;
}

interface FilterOption {
  type: string;
  date?: string;
  label: string;
  disabled: boolean;
}

/**
 * Build the ordered list of timeframe options to render as radios in the
 * availability filter modal. Options and disabled state depend on cached
 * transit results and which warehouses have inventory.
 */
function buildFilterOptions(): FilterOption[] {
  const pickupCodes = getPickupWarehouseCodes();
  const pickupHasInventory = pickupCodes.some((code) => warehouseHasInventory(code));
  const tomorrowAvailable = hasWarehouseDeliveryByTomorrow();

  const options: FilterOption[] = [
    { type: "pickup", label: "Pickup or Local Delivery", disabled: !pickupHasInventory },
    { type: "tomorrow", label: "Shipping ETA Tomorrow", disabled: !tomorrowAvailable },
  ];

  collectFutureETADates().slice(0, 2).forEach((date) => {
    options.push({
      type: "date",
      date: date.toISOString().split("T")[0],
      label: `Shipping ETA ${formatDateLabel(date)}`,
      disabled: false,
    });
  });

  options.push({ type: "week", label: "Within a Week", disabled: false });
  options.push({ type: "all", label: "All", disabled: false });

  return options;
}

/**
 * Resolve the label for the currently active filter, used on the page
 * trigger button to show the selected value.
 */
function getCurrentFilterLabel(): string {
  const match = buildFilterOptions().find(
    (o) => o.type === currentFilterType && (o.type !== "date" || o.date === currentFilterDate)
  );
  return match ? match.label : "Within a Week";
}

/**
 * Update the trigger button label (above the pricing grid) to reflect the
 * currently applied filter.
 */
function updateTriggerButtonLabel(): void {
  $(".availability-filter-trigger-value").text(getCurrentFilterLabel());
}

/**
 * Render radio options into the modal's fieldset based on current data.
 * Called on modal open and whenever transit data changes.
 */
function renderModalRadios(): void {
  const $container = $("#availabilityFilterRadios");
  if ($container.length === 0) return;

  const options = buildFilterOptions();
  const html = options
    .map((opt, i) => {
      const id = `availFilterOpt_${i}`;
      const dateAttr = opt.date ? ` data-filter-date="${opt.date}"` : "";
      const disabledAttr = opt.disabled ? " disabled" : "";
      const checked =
        opt.type === currentFilterType &&
        (opt.type !== "date" || opt.date === currentFilterDate);
      const checkedAttr = checked ? " checked" : "";
      const optionClass = opt.disabled
        ? "availability-filter-radio-option is-disabled"
        : "availability-filter-radio-option";
      return `
        <label class="${optionClass}" for="${id}">
          <input type="radio" name="availabilityFilterOption" id="${id}" value="${opt.type}"${dateAttr}${disabledAttr}${checkedAttr}>
          <span>${opt.label}</span>
        </label>`;
    })
    .join("");

  $container.html(html);

  // Ensure at least one radio is checked (falls back to week if current is now disabled)
  if ($container.find('input[type="radio"]:checked').length === 0) {
    $container.find('input[type="radio"][value="week"]').prop("checked", true);
  }
}

function getDefaultZip(): string {
  const $zipInput = $("#shipToZip");
  if ($zipInput.length > 0) {
    const defaultZip = $zipInput.data("default-zip");
    if (defaultZip && /^\d{5}$/.test(String(defaultZip))) {
      return String(defaultZip);
    }
  }
  return "70806";
}

function getCurrentZipValue(): string {
  const $z = $("#shipToZip");
  const val = (($z.val() as string | undefined) ?? "").trim();
  return /^\d{5}$/.test(val) ? val : getDefaultZip();
}

/**
 * Show/hide the Reset link next to the modal ZIP input, and apply the
 * "modified" background when the current value differs from the default.
 */
function updateModalZipModifiedState(): void {
  const $zip = $("#modalShipToZip");
  const $link = $("#availabilityFilterResetLink");
  const val = (($zip.val() as string | undefined) ?? "").trim();
  const def = getDefaultZip();

  if (val !== def && val !== "") {
    $zip.addClass("availability-filter-zip-input--modified");
    $link.show();
  } else {
    $zip.removeClass("availability-filter-zip-input--modified");
    $link.hide();
  }
}

function openModal(): void {
  renderModalRadios();
  $("#modalShipToZip").val(getCurrentZipValue());
  updateModalZipModifiedState();
  $("#availability-filter-modal").addClass("is-visible");

  // Focus the selected radio so the keyboard can move through options immediately
  setTimeout(() => {
    const $selected = $("#availabilityFilterRadios input[type='radio']:checked");
    if ($selected.length > 0) {
      ($selected[0] as HTMLElement).focus();
    }
  }, 0);
}

function closeModal(): void {
  $("#availability-filter-modal").removeClass("is-visible");
}

/**
 * Apply the form's current selections: set the filter type/date, sync the
 * ZIP back to the outer #shipToZip input (which triggers a transit reload),
 * then close the modal.
 */
function applyFromModal(): void {
  const $selected = $("#availabilityFilterRadios input[type='radio']:checked");
  if ($selected.length > 0) {
    currentFilterType = $selected.val() as string;
    currentFilterDate = $selected.attr("data-filter-date");
  }

  // Sync modal ZIP to the outer input. Blank in the modal reverts to the
  // customer default. The outer input already has a debounced "input" handler
  // that persists the value and triggers loadTransitTimes() → which in turn
  // calls onTransitDataLoaded() and re-applies the filter.
  const $outerZip = $("#shipToZip");
  const modalVal = (($("#modalShipToZip").val() as string | undefined) ?? "").trim();
  const outerVal = (($outerZip.val() as string | undefined) ?? "").trim();
  const defaultZip = getDefaultZip();

  let zipChanged = false;
  if (modalVal === "") {
    if (outerVal !== defaultZip) {
      $outerZip.val(defaultZip);
      zipChanged = true;
    }
  } else if (/^\d{5}$/.test(modalVal) && modalVal !== outerVal) {
    $outerZip.val(modalVal);
    zipChanged = true;
  }

  if (zipChanged) {
    $outerZip.trigger("input");
    // Transit reload will re-apply filter on completion; still update trigger
    // label immediately so the user sees their timeframe selection reflected.
  } else {
    applyFilter(currentFilterType, currentFilterDate);
  }

  updateTriggerButtonLabel();
  closeModal();
}

/**
 * Reset the modal form fields only (radio → "Within a Week", ZIP → default).
 * Does not apply until the user clicks Apply. Does not affect color filters.
 */
function resetModalForm(): void {
  $("#availabilityFilterRadios input[type='radio']").prop("checked", false);
  $(`#availabilityFilterRadios input[type='radio'][value="week"]`).prop("checked", true);
  $("#modalShipToZip").val(getDefaultZip());
  updateModalZipModifiedState();
}

/**
 * Apply availability filter based on selected button
 */
function applyFilter(filterType: string, filterDate?: string): void {
  const pickupCodes = getPickupWarehouseCodes();

  if (filterType === "all") {
    $(".inventory_master_table").removeClass("availability-filtering");
    $(".warehouse_row").removeClass("show-this-warehouse");
  } else {
    $(".inventory_master_table").addClass("availability-filtering");
    $(".warehouse_row").removeClass("show-this-warehouse");

    if (filterType === "pickup") {
      pickupCodes.forEach((code) => {
        $(`.warehouse_row[data-warehouse-code="${code}"]`).each(function () {
          const $row = $(this);
          if ($row.hasClass("warehouse_row_local")) {
            const $variantCells = $row.find(".variant_cell").not(".variant_cell_empty");
            const totalCells = $variantCells.length;
            const noQuantityCells = $variantCells.filter(".no-quantity").length;
            if (totalCells > 0 && noQuantityCells < totalCells) {
              $row.addClass("show-this-warehouse");
            }
          } else {
            $row.addClass("show-this-warehouse");
          }
        });
      });
    } else if (filterType === "tomorrow") {
      const tomorrow = getTomorrowDate();
      $(".warehouse_row").each(function () {
        const $row = $(this);
        const deliveryDateStr = $row.attr("data-estimated-delivery");
        if (!deliveryDateStr) return;

        const deliveryDate = parseDate(deliveryDateStr);
        if (deliveryDate && deliveryDate <= tomorrow) {
          $row.addClass("show-this-warehouse");
        }
      });
    } else if (filterType === "week") {
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      sevenDaysFromNow.setHours(23, 59, 59, 999);

      $(".warehouse_row").each(function () {
        const $row = $(this);
        const deliveryDateStr = $row.attr("data-estimated-delivery");
        // Permissive default: if we have no delivery data for this row, show
        // it rather than silently hide it. Prevents an empty grid when a
        // warehouse returns null for the customer's ZIP, when transit fails
        // to load at all, or when a lazy-loaded row gets reapplied against
        // "week" before its date has been populated. Strict filters
        // (tomorrow, specific date) still require an explicit match.
        if (!deliveryDateStr) {
          $row.addClass("show-this-warehouse");
          return;
        }

        const deliveryDate = parseDate(deliveryDateStr);
        if (deliveryDate && deliveryDate <= sevenDaysFromNow) {
          $row.addClass("show-this-warehouse");
        }
      });
    } else if (filterType === "date" && filterDate) {
      const targetDate = parseDate(filterDate);
      if (!targetDate) return;

      $(".warehouse_row").each(function () {
        const $row = $(this);
        const deliveryDateStr = $row.attr("data-estimated-delivery");
        if (!deliveryDateStr) return;

        const deliveryDate = parseDate(deliveryDateStr);
        if (deliveryDate && deliveryDate <= targetDate) {
          $row.addClass("show-this-warehouse");
        }
      });
    }
  }

  // Row visibility just changed — re-pin every Update Cart button to a
  // uniform height based on the new visible-row count for the first product.
  applyUniformUpdateCartButtonHeight();
}

/**
 * Set the active filter programmatically and apply it.
 */
function setActiveFilter(filterType: string, filterDate?: string): void {
  currentFilterType = filterType;
  currentFilterDate = filterDate;
  applyFilter(filterType, filterDate);
  updateTriggerButtonLabel();
}

/**
 * Re-apply the currently active availability filter.
 * Call this after lazy-loading new rows so they receive the correct
 * show-this-warehouse class (or lack thereof).
 */
export function reapplyCurrentFilter(): void {
  applyFilter(currentFilterType, currentFilterDate);
}

/**
 * Reset to default state ("Within a Week" selected).
 * Exposed for the outside #resetFilters link.
 */
export function reset(): void {
  setActiveFilter("week");
}

/**
 * Called when transit time data is loaded
 */
export function onTransitDataLoaded(results: WarehouseResult[]): void {
  cachedTransitResults = results;

  // Re-render modal radios so their disabled state and dynamic dates reflect
  // the fresh transit data (safe whether modal is open or closed).
  renderModalRadios();

  // Keep the mobile default aligned with desktop ("Within a Week"). The
  // stricter mobile-only pickup/tomorrow auto-selection can hide every
  // warehouse for a clicked lazy-loaded color, and the mobile timeframe
  // trigger is hidden so users have no obvious way to recover.
  setActiveFilter("week");
}

/**
 * Initialize availability filter functionality: wire trigger/modal events.
 */
export function initAvailabilityFilter(): void {
  // Open modal when the trigger button is clicked
  $(document).on("click", "#availabilityFilterTrigger", function (e: JQuery.ClickEvent) {
    e.preventDefault();
    openModal();
  });

  // Close modal via overlay, close button, or Cancel
  $(document).on(
    "click",
    ".availability-filter-modal-overlay, .availability-filter-modal-close, .availability-filter-cancel",
    function (e: JQuery.ClickEvent) {
      e.preventDefault();
      closeModal();
    }
  );

  // ESC closes the modal
  $(document).on("keydown", function (e: JQuery.KeyDownEvent) {
    if (e.key === "Escape" && $("#availability-filter-modal").hasClass("is-visible")) {
      closeModal();
    }
  });

  // Submit (Apply button or Enter key) applies the form
  $(document).on("submit", "#availabilityFilterForm", function (e: JQuery.SubmitEvent) {
    e.preventDefault();
    applyFromModal();
  });

  // Reset link (modal) — resets only the modal form fields, does not apply
  $(document).on("click", "#availabilityFilterResetLink", function (e: JQuery.ClickEvent) {
    e.preventDefault();
    resetModalForm();
  });

  // Update reset-link visibility + modified background as the user types
  $(document).on("input", "#modalShipToZip", function () {
    const $input = $(this);
    const cleaned = ($input.val() as string).replace(/\D/g, "").substring(0, 5);
    if (cleaned !== $input.val()) {
      $input.val(cleaned);
    }
    updateModalZipModifiedState();
  });

  // Initialize trigger label with default filter ("Within a Week")
  updateTriggerButtonLabel();
}
